import type { Except, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import type { ReactElement } from 'react'

import type { RawTextAreaProps } from '../../forms/textArea/RawTextArea'
import { TextArea } from '../../forms/textArea/TextArea'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikTextAreaProps = RawTextAreaProps & {
  name: ValidatedKey
} & Except<FormikInputPropsForWrapper<string | ReactElement>, 'validationText'>

/**
 * FormikTextArea to be used within the scope of Formik forms. Will extract the field's data from Formik and surround the
 * input element with a FormInput to show the field status consistently.
 */
function FormikTextAreaInternal({
  name,
  isRequired,
  label,
  description,
  disabled,
  className,
  existingValueConfig,
  ...restProps
}: FormikTextAreaProps) {
  const [field, meta] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as string

  const simpleValueDifferenceCheck = useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue,
    existingValueConfig,
    inputValueDifferenceFromExistingValue: simpleValueDifferenceCheck,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      htmlFor: name,
      error,
      className,
    },
  })

  return (
    <TextArea
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      disabled={isFieldDisabled}
      {...field}
      {...restProps}
    />
  )
}

export const FormikTextArea = withFormikInputDiffing(FormikTextAreaInternal)
